'use client';

import Link, { LinkProps } from 'next/link';
import React from 'react';

import { cn } from '@/lib/utils';

import { WithRef } from '@/components/types';

import { HTMLDataTrackerProps } from '@/app/types';
import { variantColors } from '@/decodo/components/atoms/Button/styles';
import { returnRefAndGenerateDataTracker } from '@/decodo/components/atoms/Button/utils';

const commonStyles = cn(
  'w-fit font-serif font-medium inline-flex gap-2 items-center justify-center rounded-[4px] transition-colors disabled:bg-gray-200 disabled:text-gray-500 disabled:pointer-events-none'
);
const inputVariantStyles = cn('px-4');

export const sizesOptions = ['l', 'm', 's'] as const;
const sizesStyles: Record<(typeof sizesOptions)[number], string> = {
  l: 'h-12 text-[1.125rem] px-6 lineHeight-[2rem]',
  m: 'h-10 text-[1rem] px-6 lineHeight-[1.5rem]',
  s: 'h-8 text-[0.875rem] px-4 lineHeight-[1rem]',
};

type ButtonElementProps = WithRef<
  HTMLButtonElement,
  React.ButtonHTMLAttributes<HTMLButtonElement> & {
    as?: 'button';
  }
>;
type AhrefElementProps = WithRef<
  HTMLAnchorElement,
  React.AnchorHTMLAttributes<HTMLAnchorElement> & {
    as?: 'a';
  }
>;
type NextLinkElementProps = WithRef<
  HTMLAnchorElement,
  React.AnchorHTMLAttributes<HTMLAnchorElement> &
    LinkProps & {
      as?: 'nextlink';
    }
>;

// TODO maybe add fullWidthOnMobile, cause we have pretty much in all app that kind of buttons

export const variantsOptions = ['filled', 'outlined', 'text', 'input'] as const;
export type ButtonVariants = (typeof variantsOptions)[number];
export const colorsOptions = [
  'primary',
  'secondary',
  'white',
  'destructive',
  'tertiary',
] as const;
export type ButtonColors = (typeof colorsOptions)[number];
export type BaseButtonProps = {
  children: React.ReactNode;
  variant?: ButtonVariants;
  size?: keyof typeof sizesStyles;
  color?: ButtonColors;
  slim?: boolean;
  fullwidth?: boolean;
  textWrap?: boolean;
  noStyles?: boolean;
  /**
   * Element to show on the left, usually an icon.
   */
  leftSection?: React.ReactNode;
  /**
   * Element to show on the right, usually an icon.
   */
  rightSection?: React.ReactNode;
  classNames?: {
    leftSection?: string;
    rightSection?: string;
    inner?: string;
    root?: string;
  };
};
export type ButtonElement = ButtonElementProps & BaseButtonProps;
export type AhrefElement = AhrefElementProps & BaseButtonProps;
export type NextLinkElement = NextLinkElementProps & BaseButtonProps;
export type ButtonProps = (
  | ButtonElementProps
  | AhrefElementProps
  | NextLinkElement
) &
  BaseButtonProps &
  HTMLDataTrackerProps;

export const Button = ({
  children,
  variant = 'filled',
  size = 'm',
  color = 'primary',
  slim = false,
  leftSection,
  rightSection,
  fullwidth,
  as = 'button',
  textWrap = false,
  noStyles,
  classNames,
  ref,
  ...props
}: ButtonProps) => {
  const isChildrenValid =
    React.Children.toArray(children).filter((child) => !!child).length > 0;

  const content = (
    <>
      {!!leftSection && (
        <span
          className={cn(
            'flex flex-row items-center justify-center gap-1',
            classNames?.leftSection
          )}
        >
          {leftSection}
        </span>
      )}
      {isChildrenValid && (
        <span
          className={cn(
            !noStyles && 'flex h-full items-center justify-center',
            !noStyles && !textWrap && 'whitespace-nowrap',
            classNames?.inner
          )}
        >
          {children}
        </span>
      )}
      {!!rightSection && (
        <span
          className={cn(
            'flex flex-row items-center justify-center gap-1',
            classNames?.rightSection
          )}
        >
          {rightSection}
        </span>
      )}
    </>
  );

  const conditionalClasses = cn(
    commonStyles,
    variantColors[variant]?.[color],
    sizesStyles[size],
    variant === 'input' && inputVariantStyles,
    fullwidth && 'w-full',
    slim && 'p-0 h-fit'
  );

  if (as === 'nextlink') {
    return (
      <Link
        data-tracker-type='link'
        className={cn(!noStyles && conditionalClasses, classNames?.root)}
        ref={(_ref) => {
          returnRefAndGenerateDataTracker(_ref, ref);
        }}
        {...(props as NextLinkElementProps)}
      >
        {content}
      </Link>
    );
  }

  if (as === 'a') {
    return (
      <a
        data-tracker-type='link'
        className={cn(!noStyles && conditionalClasses, classNames?.root)}
        ref={(_ref) => {
          returnRefAndGenerateDataTracker(_ref, ref);
        }}
        {...(props as AhrefElementProps)}
      >
        {content}
      </a>
    );
  }

  return (
    <button
      data-tracker-type='button'
      className={cn(!noStyles && conditionalClasses, classNames?.root)}
      ref={(_ref) => {
        returnRefAndGenerateDataTracker(_ref, ref);
      }}
      {...(props as ButtonElementProps)}
    >
      {content}
    </button>
  );
};

export default Button;
